/**
 * UK city data for SEO location pages.
 *
 * Each city has:
 *   - slug, name, region, regionShort, population (rounded)
 *   - postcodeOutwardSamples  (used in Areas Covered + local FAQs)
 *   - neighbourhoods          (used in Areas Covered chips)
 *   - landmarks               (optional flavour)
 *   - coords {lat, lng}       (used in LocalBusiness JSON-LD)
 *   - nearbyCities            (slugs — used for Related Locations links)
 *   - transportContext        (one-sentence local transport challenge — used in intros)
 *   - distanceFromLondonMiles (used for service-from-city FAQs)
 *
 * Phase 1 ships with the top 20 UK metro areas. The content layer is
 * structured so adding a city is a pure data append — no template changes
 * required.
 */

export const CITIES = {
  london: {
    slug: "london",
    name: "London",
    region: "Greater London",
    regionShort: "London",
    population: "9.5 million",
    postcodeOutwardSamples: ["E1", "EC1", "N1", "SE1", "SW1", "W1", "NW1"],
    neighbourhoods: ["Westminster", "Camden", "Hackney", "Islington", "Lambeth", "Southwark", "Tower Hamlets", "Wandsworth", "Greenwich", "Lewisham", "Hammersmith", "Kensington"],
    landmarks: ["the City of London", "Canary Wharf", "Heathrow Airport"],
    coords: { lat: 51.5074, lng: -0.1278 },
    nearbyCities: ["southampton", "leicester", "coventry", "birmingham"],
    transportContext: "ULEZ regulations, complex congestion zones and dense central postcodes",
    distanceFromLondonMiles: 0,
  },
  manchester: {
    slug: "manchester",
    name: "Manchester",
    region: "Greater Manchester",
    regionShort: "Greater Manchester",
    population: "2.8 million",
    postcodeOutwardSamples: ["M1", "M2", "M3", "M4", "M14", "M15", "M16", "M20"],
    neighbourhoods: ["Northern Quarter", "Ancoats", "Salford", "Didsbury", "Chorlton", "Trafford", "Stockport", "Oldham"],
    landmarks: ["Manchester city centre", "MediaCity UK", "Old Trafford"],
    coords: { lat: 53.4808, lng: -2.2426 },
    nearbyCities: ["liverpool", "leeds", "sheffield", "bradford"],
    transportContext: "the Clean Air Zone (suspended), tram-line access constraints and a busy ring road",
    distanceFromLondonMiles: 200,
  },
  birmingham: {
    slug: "birmingham",
    name: "Birmingham",
    region: "West Midlands",
    regionShort: "the West Midlands",
    population: "2.6 million",
    postcodeOutwardSamples: ["B1", "B2", "B3", "B4", "B5", "B15", "B16", "B17"],
    neighbourhoods: ["Digbeth", "Jewellery Quarter", "Edgbaston", "Moseley", "Harborne", "Solihull", "Sutton Coldfield", "Erdington"],
    landmarks: ["the Bullring", "Birmingham New Street", "the NEC"],
    coords: { lat: 52.4862, lng: -1.8904 },
    nearbyCities: ["coventry", "wolverhampton", "leicester", "nottingham"],
    transportContext: "the Clean Air Zone, an extensive ring road system and central pedestrianised zones",
    distanceFromLondonMiles: 120,
  },
  leeds: {
    slug: "leeds",
    name: "Leeds",
    region: "West Yorkshire",
    regionShort: "West Yorkshire",
    population: "800,000",
    postcodeOutwardSamples: ["LS1", "LS2", "LS3", "LS6", "LS8", "LS9", "LS11", "LS12"],
    neighbourhoods: ["Headingley", "Roundhay", "Chapel Allerton", "Kirkstall", "Holbeck", "Beeston", "Morley", "Pudsey"],
    landmarks: ["Leeds city centre", "Elland Road", "the Leeds-Bradford Airport"],
    coords: { lat: 53.8008, lng: -1.5491 },
    nearbyCities: ["bradford", "sheffield", "manchester", "nottingham"],
    transportContext: "the Clean Air Zone (cancelled), the M62 corridor and Victorian-era city-centre layout",
    distanceFromLondonMiles: 200,
  },
  liverpool: {
    slug: "liverpool",
    name: "Liverpool",
    region: "Merseyside",
    regionShort: "Merseyside",
    population: "900,000",
    postcodeOutwardSamples: ["L1", "L2", "L3", "L4", "L8", "L15", "L18", "L25"],
    neighbourhoods: ["Liverpool ONE", "Baltic Triangle", "Anfield", "Toxteth", "Aigburth", "Wavertree", "Crosby", "Bootle"],
    landmarks: ["the Albert Dock", "Anfield", "Liverpool John Lennon Airport"],
    coords: { lat: 53.4084, lng: -2.9916 },
    nearbyCities: ["manchester", "leeds", "bradford", "sheffield"],
    transportContext: "tunnel access constraints under the Mersey and dock-area HGV restrictions",
    distanceFromLondonMiles: 215,
  },
  sheffield: {
    slug: "sheffield",
    name: "Sheffield",
    region: "South Yorkshire",
    regionShort: "South Yorkshire",
    population: "580,000",
    postcodeOutwardSamples: ["S1", "S2", "S3", "S4", "S7", "S8", "S10", "S11"],
    neighbourhoods: ["Kelham Island", "Ecclesall", "Hillsborough", "Crookes", "Fulwood", "Broomhill", "Heeley", "Norton"],
    landmarks: ["the Crucible", "Sheffield railway station", "the Peak District National Park edge"],
    coords: { lat: 53.3811, lng: -1.4701 },
    nearbyCities: ["leeds", "bradford", "manchester", "nottingham"],
    transportContext: "hilly terrain, tram-line corridors and Peak District access roads",
    distanceFromLondonMiles: 170,
  },
  bristol: {
    slug: "bristol",
    name: "Bristol",
    region: "South West England",
    regionShort: "the South West",
    population: "470,000",
    postcodeOutwardSamples: ["BS1", "BS2", "BS3", "BS4", "BS5", "BS6", "BS7", "BS8"],
    neighbourhoods: ["Clifton", "Stokes Croft", "Bedminster", "Easton", "Redland", "Southville", "Montpelier", "Horfield"],
    landmarks: ["the Suspension Bridge", "Temple Meads", "the Floating Harbour"],
    coords: { lat: 51.4545, lng: -2.5879 },
    nearbyCities: ["southampton", "plymouth", "cardiff", "coventry"],
    transportContext: "the Clean Air Zone, the M5 corridor and historic narrow streets in the central area",
    distanceFromLondonMiles: 120,
  },
  glasgow: {
    slug: "glasgow",
    name: "Glasgow",
    region: "Scotland",
    regionShort: "Scotland",
    population: "630,000",
    postcodeOutwardSamples: ["G1", "G2", "G3", "G4", "G5", "G11", "G12", "G20"],
    neighbourhoods: ["Merchant City", "West End", "Southside", "Govan", "Partick", "Dennistoun", "Shawlands", "Maryhill"],
    landmarks: ["George Square", "Glasgow Central station", "Glasgow Airport"],
    coords: { lat: 55.8642, lng: -4.2518 },
    nearbyCities: ["edinburgh", "newcastle", "belfast", "bradford"],
    transportContext: "the Low Emission Zone, the M8 corridor and tenement-block access constraints",
    distanceFromLondonMiles: 410,
  },
  edinburgh: {
    slug: "edinburgh",
    name: "Edinburgh",
    region: "Scotland",
    regionShort: "Scotland",
    population: "525,000",
    postcodeOutwardSamples: ["EH1", "EH2", "EH3", "EH4", "EH6", "EH7", "EH8", "EH10"],
    neighbourhoods: ["Old Town", "New Town", "Leith", "Stockbridge", "Morningside", "Bruntsfield", "Portobello", "Newington"],
    landmarks: ["Edinburgh Castle", "Princes Street", "Edinburgh Airport"],
    coords: { lat: 55.9533, lng: -3.1883 },
    nearbyCities: ["glasgow", "newcastle", "belfast", "leeds"],
    transportContext: "the Old Town's narrow cobbled streets, tram-line restrictions and tight residential parking",
    distanceFromLondonMiles: 400,
  },
  newcastle: {
    slug: "newcastle",
    name: "Newcastle",
    region: "Tyne and Wear",
    regionShort: "the North East",
    population: "300,000",
    postcodeOutwardSamples: ["NE1", "NE2", "NE3", "NE4", "NE6", "NE7", "NE8", "NE15"],
    neighbourhoods: ["Quayside", "Jesmond", "Heaton", "Gosforth", "Byker", "Walker", "Fenham", "Gateshead"],
    landmarks: ["the Tyne Bridge", "Newcastle Central station", "Newcastle International Airport"],
    coords: { lat: 54.9783, lng: -1.6178 },
    nearbyCities: ["leeds", "bradford", "edinburgh", "glasgow"],
    transportContext: "Clean Air Zone fees, the Tyne tunnels and steep approaches around the Quayside",
    distanceFromLondonMiles: 280,
  },
  nottingham: {
    slug: "nottingham",
    name: "Nottingham",
    region: "East Midlands",
    regionShort: "the East Midlands",
    population: "330,000",
    postcodeOutwardSamples: ["NG1", "NG2", "NG3", "NG4", "NG5", "NG7", "NG8", "NG9"],
    neighbourhoods: ["The Lace Market", "West Bridgford", "Sherwood", "Carrington", "Beeston", "Wollaton", "Mapperley", "Bulwell"],
    landmarks: ["Nottingham Castle", "Trent Bridge", "Nottingham railway station"],
    coords: { lat: 52.9548, lng: -1.1581 },
    nearbyCities: ["leicester", "birmingham", "sheffield", "coventry"],
    transportContext: "tram-line restrictions across the central area and the ring-road network",
    distanceFromLondonMiles: 130,
  },
  cardiff: {
    slug: "cardiff",
    name: "Cardiff",
    region: "Wales",
    regionShort: "South Wales",
    population: "365,000",
    postcodeOutwardSamples: ["CF1", "CF3", "CF5", "CF10", "CF11", "CF14", "CF23", "CF24"],
    neighbourhoods: ["Cardiff Bay", "Cathays", "Roath", "Pontcanna", "Canton", "Splott", "Whitchurch", "Llanishen"],
    landmarks: ["Cardiff Castle", "the Principality Stadium", "Cardiff Airport"],
    coords: { lat: 51.4816, lng: -3.1791 },
    nearbyCities: ["bristol", "southampton", "plymouth", "coventry"],
    transportContext: "the M4 corridor, central pedestrianisation and arena event-day restrictions",
    distanceFromLondonMiles: 150,
  },
  belfast: {
    slug: "belfast",
    name: "Belfast",
    region: "Northern Ireland",
    regionShort: "Northern Ireland",
    population: "340,000",
    postcodeOutwardSamples: ["BT1", "BT2", "BT3", "BT4", "BT5", "BT7", "BT9", "BT12"],
    neighbourhoods: ["Cathedral Quarter", "Titanic Quarter", "Botanic", "Stranmillis", "Ormeau", "Falls", "Shankill", "East Belfast"],
    landmarks: ["Titanic Belfast", "Belfast City Hall", "Belfast International Airport"],
    coords: { lat: 54.5973, lng: -5.9301 },
    nearbyCities: ["glasgow", "edinburgh", "liverpool", "newcastle"],
    transportContext: "ferry-routed access to the UK mainland and the West Link corridor",
    distanceFromLondonMiles: 520,
  },
  leicester: {
    slug: "leicester",
    name: "Leicester",
    region: "East Midlands",
    regionShort: "the East Midlands",
    population: "370,000",
    postcodeOutwardSamples: ["LE1", "LE2", "LE3", "LE4", "LE5", "LE6", "LE7", "LE8"],
    neighbourhoods: ["Cultural Quarter", "Clarendon Park", "Stoneygate", "Knighton", "Belgrave", "Aylestone", "Evington", "Oadby"],
    landmarks: ["Leicester railway station", "King Power Stadium", "the Curve"],
    coords: { lat: 52.6369, lng: -1.1398 },
    nearbyCities: ["nottingham", "coventry", "birmingham", "sheffield"],
    transportContext: "central pedestrianisation, ring-road bottlenecks and tight terraced-street parking",
    distanceFromLondonMiles: 100,
  },
  coventry: {
    slug: "coventry",
    name: "Coventry",
    region: "West Midlands",
    regionShort: "the West Midlands",
    population: "345,000",
    postcodeOutwardSamples: ["CV1", "CV2", "CV3", "CV4", "CV5", "CV6", "CV7", "CV8"],
    neighbourhoods: ["City Centre", "Earlsdon", "Cheylesmore", "Coundon", "Stoke", "Tile Hill", "Walsgrave", "Allesley"],
    landmarks: ["Coventry Cathedral", "Coventry railway station", "the Ricoh Arena"],
    coords: { lat: 52.4068, lng: -1.5197 },
    nearbyCities: ["birmingham", "leicester", "wolverhampton", "nottingham"],
    transportContext: "the central ring road and pedestrian-priority city core",
    distanceFromLondonMiles: 95,
  },
  bradford: {
    slug: "bradford",
    name: "Bradford",
    region: "West Yorkshire",
    regionShort: "West Yorkshire",
    population: "540,000",
    postcodeOutwardSamples: ["BD1", "BD2", "BD3", "BD4", "BD5", "BD7", "BD8", "BD9"],
    neighbourhoods: ["City Centre", "Manningham", "Heaton", "Eccleshill", "Bowling", "Wibsey", "Idle", "Saltaire"],
    landmarks: ["Bradford Interchange", "the National Science and Media Museum", "Saltaire"],
    coords: { lat: 53.7960, lng: -1.7594 },
    nearbyCities: ["leeds", "sheffield", "manchester", "liverpool"],
    transportContext: "the Clean Air Zone, hilly terrain and Victorian-era street patterns",
    distanceFromLondonMiles: 200,
  },
  "stoke-on-trent": {
    slug: "stoke-on-trent",
    name: "Stoke-on-Trent",
    region: "Staffordshire",
    regionShort: "Staffordshire",
    population: "260,000",
    postcodeOutwardSamples: ["ST1", "ST2", "ST3", "ST4", "ST5", "ST6", "ST7", "ST11"],
    neighbourhoods: ["Hanley", "Burslem", "Tunstall", "Longton", "Fenton", "Stoke", "Trentham", "Newcastle-under-Lyme"],
    landmarks: ["the bet365 Stadium", "Wedgwood Visitor Centre", "Stoke-on-Trent railway station"],
    coords: { lat: 53.0027, lng: -2.1794 },
    nearbyCities: ["wolverhampton", "birmingham", "manchester", "liverpool"],
    transportContext: "the M6 corridor and the six-town conurbation with multiple central districts",
    distanceFromLondonMiles: 150,
  },
  wolverhampton: {
    slug: "wolverhampton",
    name: "Wolverhampton",
    region: "West Midlands",
    regionShort: "the West Midlands",
    population: "265,000",
    postcodeOutwardSamples: ["WV1", "WV2", "WV3", "WV4", "WV6", "WV10", "WV11", "WV14"],
    neighbourhoods: ["City Centre", "Tettenhall", "Penn", "Wednesfield", "Bilston", "Codsall", "Compton", "Whitmore Reans"],
    landmarks: ["Wolverhampton railway station", "Molineux Stadium", "the Mander Centre"],
    coords: { lat: 52.5870, lng: -2.1287 },
    nearbyCities: ["birmingham", "coventry", "stoke-on-trent", "leicester"],
    transportContext: "the M6 / M54 corridor and dense central retail areas",
    distanceFromLondonMiles: 130,
  },
  plymouth: {
    slug: "plymouth",
    name: "Plymouth",
    region: "Devon",
    regionShort: "the South West",
    population: "265,000",
    postcodeOutwardSamples: ["PL1", "PL2", "PL3", "PL4", "PL5", "PL6", "PL7", "PL9"],
    neighbourhoods: ["The Barbican", "Mutley", "Plympton", "Plymstock", "Stoke", "Devonport", "Mannamead", "Crownhill"],
    landmarks: ["Plymouth Hoe", "the Barbican", "Plymouth railway station"],
    coords: { lat: 50.3755, lng: -4.1427 },
    nearbyCities: ["bristol", "cardiff", "southampton", "coventry"],
    transportContext: "the long A38 corridor west of Bristol and ferry-port access to the Continent",
    distanceFromLondonMiles: 240,
  },
  southampton: {
    slug: "southampton",
    name: "Southampton",
    region: "Hampshire",
    regionShort: "the South Coast",
    population: "270,000",
    postcodeOutwardSamples: ["SO14", "SO15", "SO16", "SO17", "SO18", "SO19", "SO31", "SO32"],
    neighbourhoods: ["City Centre", "Bevois Valley", "Highfield", "Portswood", "Bitterne", "Shirley", "Woolston", "Bassett"],
    landmarks: ["Southampton Central station", "the Port of Southampton", "Southampton Airport"],
    coords: { lat: 50.9097, lng: -1.4044 },
    nearbyCities: ["london", "bristol", "plymouth", "cardiff"],
    transportContext: "the Clean Air Zone, cruise-terminal traffic and the M27 corridor",
    distanceFromLondonMiles: 80,
  },
};

export const CITY_LIST = Object.values(CITIES);

export function getCity(slug) {
  return CITIES[slug] || null;
}
